import { Box, Checkbox, Divider, Flex, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { SelectedClientPill } from 'src/design-system/SelectedClientPill';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { useClients } from '../hooks/useClients';
import { isMemberSelected } from './dataHelpers';

const CONDENSED_THRESHOLD = 3;

type Props = {
  selectedMembers: Client[];
  onToggle: (client: Client) => void;
  searchTerm?: string;
  noScroll?: boolean;
  excludeInFamilies?: boolean;
};

export default function MemberSearchList({
  selectedMembers,
  onToggle,
  searchTerm,
  noScroll,
  excludeInFamilies,
}: Props) {
  const { clients } = useClients();
  const [isExpanded, setIsExpanded] = useState(false);

  const words = searchTerm?.trim() ? searchTerm.toLowerCase().split(/\s+/).filter(Boolean) : [];

  const filtered = clients.filter(client => {
    if (excludeInFamilies && client.familyId) return false;
    if (isMemberSelected(client, selectedMembers)) return false;
    if (words.length === 0) return true;
    const fullName = formatName(
      client.firstName,
      client.lastName,
      NameFormat.NATURAL,
    ).toLowerCase();
    const email = (client.emailAddress ?? '').toLowerCase();
    const address = (client.personalDetails?.address ?? '').toLowerCase();
    return words.every(
      word => fullName.includes(word) || email.includes(word) || address.includes(word),
    );
  });

  const isCondensed = selectedMembers.length > CONDENSED_THRESHOLD && !isExpanded;

  const condensedLabel = isCondensed
    ? (() => {
        const first = formatName(
          selectedMembers[0].firstName,
          selectedMembers[0].lastName,
          NameFormat.NATURAL,
        );
        const second = formatName(
          selectedMembers[1].firstName,
          selectedMembers[1].lastName,
          NameFormat.NATURAL,
        );
        const remaining = selectedMembers.length - 2;
        return `${first}, ${second} and ${remaining} more`;
      })()
    : null;

  return (
    <Box
      p="sm"
      bd="var(--solid-border)"
      style={{
        borderRadius: 12,
        height: noScroll ? undefined : '100%',
        overflowY: noScroll ? undefined : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xxs)',
      }}
    >
      {selectedMembers.length > 0 && (
        <>
          <Flex wrap="wrap" gap="xxs" pb="sm" align="center">
            {isCondensed ? (
              <Flex
                align="center"
                gap="xs"
                px="md"
                py="xs"
                bg="gray.0"
                style={{ borderRadius: 20, cursor: 'pointer' }}
                onClick={() => setIsExpanded(true)}
              >
                <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                  {condensedLabel}
                </Text>
                <IconChevronDown size={16} />
              </Flex>
            ) : (
              <>
                {selectedMembers.map(client => (
                  <SelectedClientPill
                    key={client.id}
                    label={formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                    onClick={() => onToggle(client)}
                  />
                ))}
                {selectedMembers.length > CONDENSED_THRESHOLD && (
                  <Flex
                    align="center"
                    gap="xxs"
                    px="sm"
                    py="xs"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setIsExpanded(false)}
                  >
                    <Text size="sm" c="gray.7">
                      Show less
                    </Text>
                    <IconChevronUp size={16} />
                  </Flex>
                )}
              </>
            )}
          </Flex>
          <Divider mx="sm" color="gray.0" size={1} />
        </>
      )}
      {filtered.length === 0 && (
        <Text size="sm" c="gray.5" fw={FONT_WEIGHTS.medium} ta="center" py="md">
          No clients without families
        </Text>
      )}
      {filtered.map(client => {
        const email = client.emailAddress ?? '';
        const address = client.personalDetails?.address ?? '';
        const subtext = [email, address].filter(Boolean).join(' • ');

        return (
          <Box
            key={client.id}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--mantine-spacing-sm)',
            }}
            onClick={() => onToggle(client)}
          >
            <Checkbox
              checked={false}
              onChange={() => onToggle(client)}
              onClick={e => e.stopPropagation()}
              radius="sm"
              pl="md"
            />
            <Stack gap={0} py="xxs">
              <Text fw={FONT_WEIGHTS.semibold} size="sm" c="gray.10" pb="xxxs">
                {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
              </Text>
              {subtext && (
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
                  {subtext}
                </Text>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}
